import { useEffect, useState, useRef, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";

const GUEST_MODE_KEY = 'wellness_cowgirl_guest_mode';
const AUTH_STABLE_DELAY_MS = 100; // Grace period for token refresh
const ACTIVITY_STAMP_PREFIX = 'wellness_cowgirl_activity_date_';

/**
 * `last_activity_date` is a DATE column, so re-writing it on every page load
 * is a no-op update that still costs a row write. Remember locally (per user)
 * which day we already stamped and skip the round trip for the rest of it.
 */
const markActivityToday = (userId: string) => {
  const today = new Date().toISOString().split('T')[0];
  const key = ACTIVITY_STAMP_PREFIX + userId;
  try {
    if (localStorage.getItem(key) === today) return;
  } catch {
    // storage unavailable — fall through and write
  }
  supabase
    .from('profiles')
    .update({ last_activity_date: today })
    .eq('id', userId)
    .then(({ error }) => {
      if (!error) {
        try { localStorage.setItem(key, today); } catch { /* ignore */ }
      }
    });
};

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isGuest: boolean;
  isAuthStable: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Internal auth state machine. Runs EXACTLY ONCE, inside <AuthProvider>.
 * Previously this lived directly in useAuth(), meaning every one of the ~74
 * call sites created its own session listener, its own getSession() request
 * and its own `isAuthStable` timer — components disagreed about when it was
 * safe to fetch, which caused intermittent "Hay friend!" / empty-checklist
 * renders. One shared source removes those races.
 */
const useAuthState = (): AuthState => {

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthStable, setIsAuthStable] = useState(false);
  const navigate = useNavigate();
  
  const isGuest = localStorage.getItem(GUEST_MODE_KEY) === 'true';
  
  // Track previous user to detect token refresh vs logout
  const previousUserRef = useRef<User | null>(null);
  const stabilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we've already updated last_activity_date this session
  const hasUpdatedActivityRef = useRef<boolean>(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Clear any pending stability timeout
        if (stabilityTimeoutRef.current) {
          clearTimeout(stabilityTimeoutRef.current);
        }
        
        const newUser = session?.user ?? null;
        const hadPreviousUser = previousUserRef.current !== null;
        
        // If we had a user and now don't, add a grace period before updating state
        // This prevents flash during token refresh where user briefly becomes null
        if (hadPreviousUser && !newUser && event === 'TOKEN_REFRESHED') {
          // During token refresh, don't immediately null out the user
          setIsAuthStable(false);
          stabilityTimeoutRef.current = setTimeout(() => {
            // Only update if still no user after grace period
            supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
              if (!currentSession?.user) {
                setSession(null);
                setUser(null);
                previousUserRef.current = null;
              }
              setIsAuthStable(true);
            });
          }, AUTH_STABLE_DELAY_MS);
          return;
        }
        
        setSession(session);
        setUser(newUser);
        previousUserRef.current = newUser;
        setLoading(false);
        
        // Mark auth as stable after a brief delay to ensure no rapid state changes
        setIsAuthStable(false);
        stabilityTimeoutRef.current = setTimeout(() => {
          setIsAuthStable(true);
        }, AUTH_STABLE_DELAY_MS);
        
        // Clear guest mode when authenticated session exists
        if (session?.user) {
          localStorage.removeItem('wellness_cowgirl_guest_mode');
          localStorage.removeItem('wellness_cowgirl_guest_tour_completed');
          
          // Stamp today's activity at most once per session (and once per day)
          if (!hasUpdatedActivityRef.current) {
            hasUpdatedActivityRef.current = true;
            markActivityToday(session.user.id);
          }
        }
      }
    );

    // THEN check for existing session — with an 8s timeout so a hung
    // network request can't trap the user on an infinite loading screen.
    // If the timeout wins, we surface loading=false so the app routes to /auth
    // instead of showing a dead spinner forever.
    const SESSION_TIMEOUT_MS = 8000;
    let sessionResolved = false;

    const sessionTimeoutId = setTimeout(() => {
      if (sessionResolved) return;
      sessionResolved = true;
      console.warn('[useAuth] getSession() timed out after 8s — forcing loading=false');
      setSession(null);
      setUser(null);
      previousUserRef.current = null;
      setLoading(false);
      setIsAuthStable(true);
    }, SESSION_TIMEOUT_MS);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (sessionResolved) return;
      sessionResolved = true;
      clearTimeout(sessionTimeoutId);

      setSession(session);
      setUser(session?.user ?? null);
      previousUserRef.current = session?.user ?? null;
      setLoading(false);

      // Mark auth as stable after initial load
      setTimeout(() => {
        setIsAuthStable(true);
      }, AUTH_STABLE_DELAY_MS);

      // Stamp today's activity (skipped if the listener above already did)
      if (session?.user && !hasUpdatedActivityRef.current) {
        hasUpdatedActivityRef.current = true;
        markActivityToday(session.user.id);
      }
    }).catch((err) => {
      if (sessionResolved) return;
      sessionResolved = true;
      clearTimeout(sessionTimeoutId);
      console.error('[useAuth] getSession() failed:', err);

      // NOTE: Do NOT purge localStorage tokens here. Let the Supabase SDK manage
      // its own token lifecycle. Aggressively deleting tokens on any getSession
      // error caused logouts for healthy sessions when transient network blips
      // occurred. If the token is truly dead, the SDK returns null and we
      // gracefully send the user to /auth.

      setSession(null);
      setUser(null);
      previousUserRef.current = null;
      setLoading(false);
      setIsAuthStable(true);
    });

    return () => {
      subscription.unsubscribe();
      if (stabilityTimeoutRef.current) {
        clearTimeout(stabilityTimeoutRef.current);
      }
    };
  }, []);

  const signOut = async () => {
    try {
      // Soft logout: Clear local Supabase auth state without revoking refresh tokens
      // This preserves the biometric refresh token for re-login
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
          localStorage.removeItem(key);
        }
      });
      setSession(null);
      setUser(null);
      previousUserRef.current = null;
      setIsAuthStable(true);
    } catch (error) {
      console.error("Failed to clear auth tokens on sign out:", error);
    } finally {
      navigate("/auth");
    }
  };

  return { user, session, loading, signOut, isGuest, isAuthStable };
};

/**
 * Mount once, inside the router (the auth state machine uses useNavigate).
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Shared auth state. Identical return shape to the previous per-component
 * hook, so every existing call site works unchanged.
 */
export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
};

